import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/navigation/AdminSidebar';

import Button from '../../components/ui/Button';
import StatCard from './components/StatCard';
import PendingApprovalCard from './components/PendingApprovalCard';
import UserTable from './components/UserTable';
import RevealRequestCard from './components/RevealRequestCard';
import SuspiciousActivityCard from './components/SuspiciousActivityCard';
import AuditLogTable from './components/AuditLogTable';
import WaitlistCard from './components/WaitlistCard';
import AnalyticsChart from './components/AnalyticsChart';
import DemoScheduler from './components/DemoScheduler';
import AdminNotesPanel from './components/AdminNotesPanel';
import FollowUpStatusTracker from './components/FollowUpStatusTracker';
import Icon from '../../components/AppIcon';
import { accessRequestService } from '../../services/accessRequestService';
import { matchesService, candidatesService, opportunitiesService } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';
import VerificationDashboard from './components/VerificationDashboard';


const AdminControlPanel = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [accessRequests, setAccessRequests] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    pendingApprovals: 0,
    activeMatches: 0,
    revealRequests: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showDemoScheduler, setShowDemoScheduler] = useState(false);
  const [adminNotes, setAdminNotes] = useState({});
  const [followUpStatuses, setFollowUpStatuses] = useState({});
  const [waitlistRequests, setWaitlistRequests] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadWaitlistData = async () => {
    try {
      const requests = await accessRequestService?.getAll();
      setWaitlistRequests(requests?.filter((r) => r?.status === 'pending') || []);
    } catch (err) {
      console.error('Error loading waitlist data:', err);
    }
  };

  // Check admin access
  useEffect(() => {
    // Allow access if user is admin OR if email is jonathan@blindrecruit.com
    const isAdminUser = userProfile?.role === 'admin' || user?.email === 'jonathan@blindrecruit.com';
    
    if (!user || !isAdminUser) {
      console.log('Access denied. User:', user?.email, 'Role:', userProfile?.role);
      navigate('/access-request');
    } else {
      console.log('Admin access granted for:', user?.email);
    }
  }, [user, userProfile, navigate]);

  // Load admin data from Supabase
  useEffect(() => {
    const loadAdminData = async () => {
      if (!user || userProfile?.role !== 'admin') return;

      try {
        setLoading(true);

        // Load access requests
        const requests = await accessRequestService?.getAll();
        setAccessRequests(requests);

        // Load statistics
        const candidates = await candidatesService?.getAll();
        const opportunities = await opportunitiesService?.getAll();
        const matches = await matchesService?.getByUserId(user?.id);

        setStats({
          totalUsers: requests?.length,
          pendingApprovals: requests?.filter((r) => r?.status === 'pending')?.length,
          activeMatches: matches?.length,
          revealRequests: 0
        });
      } catch (err) {
        setError(err?.message);
      } finally {
        setLoading(false);
      }
    };

    loadAdminData();
  }, [user, userProfile]);

  // Load data on component mount
  useEffect(() => {
    loadWaitlistData();
  }, []);

  // Refresh function
  const handleRefresh = () => {
    setIsRefreshing(true);
    loadWaitlistData();
    setTimeout(() => {
      setIsRefreshing(false);
    }, 500);
  };

  // Mock data for pending approvals
  const pendingApprovals = [
  {
    id: 1,
    type: "candidate",
    title: "Sarah Mitchell - Senior Software Engineer",
    description: "Candidate with 12 years experience in full-stack development seeking access to platform. Strong background in React, Node.js, and cloud architecture.",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17784c577-1763297418164.png",
    avatarAlt: "Professional headshot of Caucasian woman with shoulder-length brown hair wearing navy blazer",
    submittedDate: "2 hours ago",
    priority: "high",
    metadata: ["12+ years exp", "Tech Industry", "Toronto, ON"]
  },
  {
    id: 2,
    type: "employer",
    title: "TechCorp Solutions Inc.",
    description: "Technology company seeking to hire senior developers. Verified direct employer with 500+ employees and active hiring needs.",
    avatar: "https://images.unsplash.com/photo-1721240608299-cdf5ee26c235",
    avatarAlt: "Modern glass office building exterior with blue sky reflecting in windows",
    submittedDate: "5 hours ago",
    priority: "medium",
    metadata: ["500+ employees", "Direct Employer", "Verified"],
    teamSize: "51-100",
    hrBudget: "500k-1m",
    companyName: "TechCorp Solutions Inc.",
    contactEmail: "hr@techcorp.com"
  },
  {
    id: 3,
    type: "reveal",
    title: "Identity Reveal - Construction Manager",
    description: "Employer requesting identity reveal for matched candidate. Both parties have expressed mutual interest.",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13a48293d-1763296098326.png",
    avatarAlt: "Professional headshot of Asian man with short black hair wearing gray suit",
    submittedDate: "1 day ago",
    priority: "high",
    metadata: ["Mutual Match", "Consent Granted", "Construction"]
  }];


  // Mock data for candidates
  const candidates = [
  {
    id: 1,
    name: "Michael Chen",
    email: "m.chen@email.com",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_148d49299-1763293795533.png",
    avatarAlt: "Professional headshot of Asian man with short black hair wearing navy suit",
    status: "active",
    joinedDate: "Jan 5, 2026",
    lastActivity: "2 hours ago"
  },
  {
    id: 2,
    name: "Emily Rodriguez",
    email: "e.rodriguez@email.com",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_162a57531-1763296100992.png",
    avatarAlt: "Professional headshot of Hispanic woman with long dark hair wearing white blouse",
    status: "active",
    joinedDate: "Jan 8, 2026",
    lastActivity: "5 hours ago"
  },
  {
    id: 3,
    name: "David Thompson",
    email: "d.thompson@email.com",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_129cc3c7b-1763293178950.png",
    avatarAlt: "Professional headshot of Caucasian man with gray hair wearing charcoal suit",
    status: "pending",
    joinedDate: "Jan 10, 2026",
    lastActivity: "1 day ago"
  },
  {
    id: 4,
    name: "Lisa Anderson",
    email: "l.anderson@email.com",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17faa8e7a-1763295754680.png",
    avatarAlt: "Professional headshot of African American woman with curly hair wearing burgundy blazer",
    status: "suspended",
    joinedDate: "Dec 28, 2025",
    lastActivity: "3 days ago"
  }];


  // Mock data for employers
  const employers = [
  {
    id: 1,
    name: "BuildRight Construction",
    email: "hr@buildright.com",
    avatar: "https://images.unsplash.com/photo-1685978085094-996ac4bd1748",
    avatarAlt: "Construction site with yellow crane against blue sky and modern building framework",
    status: "active",
    joinedDate: "Dec 15, 2025",
    lastActivity: "1 hour ago"
  },
  {
    id: 2,
    name: "InnovateTech Systems",
    email: "recruiting@innovatetech.com",
    avatar: "https://images.unsplash.com/photo-1663817131635-8e04e87e5d76",
    avatarAlt: "Modern glass office building with reflective windows showing city skyline",
    status: "active",
    joinedDate: "Jan 2, 2026",
    lastActivity: "3 hours ago"
  },
  {
    id: 3,
    name: "Premier Properties Group",
    email: "talent@premierproperties.com",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_180f6eda5-1766560944336.png",
    avatarAlt: "Luxury residential building exterior with modern architecture and landscaped entrance",
    status: "pending",
    joinedDate: "Jan 9, 2026",
    lastActivity: "2 days ago"
  }];


  // Mock data for reveal requests
  const revealRequests = [
  {
    id: 1,
    employerName: "Sarah Johnson",
    employerCompany: "TechCorp Solutions",
    employerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16e75c406-1763294340369.png",
    employerAvatarAlt: "Professional headshot of Caucasian woman with blonde hair wearing navy blazer",
    candidateIndustry: "Software Engineering",
    requestedDate: "Jan 10, 2026",
    message: "We are very impressed with this candidate\'s profile and would like to schedule an interview. The experience and skills align perfectly with our Senior Developer position.",
    candidateConsent: true
  },
  {
    id: 2,
    employerName: "Robert Martinez",
    employerCompany: "BuildRight Construction",
    employerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_145ad4bba-1763294926258.png",
    employerAvatarAlt: "Professional headshot of Hispanic man with short dark hair wearing gray suit",
    candidateIndustry: "Construction Management",
    requestedDate: "Jan 9, 2026",
    message: "This candidate\'s experience in large-scale commercial projects is exactly what we need for our upcoming development. Would like to discuss opportunities.",
    candidateConsent: false
  }];


  // Mock data for suspicious activities
  const suspiciousActivities = [
  {
    id: 1,
    title: "Potential Anonymity Breach Detected",
    userName: "John Smith",
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e893ff19-1763296800030.png",
    userAvatarAlt: "Professional headshot of Caucasian man with brown hair wearing blue shirt",
    severity: "critical",
    violationType: "anonymity-breach",
    detectedDate: "2 hours ago",
    description: "User attempted to share personal contact information in profile description field.",
    flaggedContent: "You can reach me at john.smith@email.com or call 555-0123",
    violations: ["Email Address", "Phone Number", "Real Name"]
  },
  {
    id: 2,
    title: "Circumvention Attempt",
    userName: "Jane Doe",
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3dab36c-1763297464267.png",
    userAvatarAlt: "Professional headshot of African American woman with short hair wearing white blouse",
    severity: "high",
    violationType: "circumvention",
    detectedDate: "5 hours ago",
    description: "Multiple attempts to bypass anonymity filters by encoding contact information.",
    flaggedContent: "Connect with me on LinkedIn: linkedin dot com slash in slash janedoe",
    violations: ["LinkedIn URL", "Circumvention Attempt"]
  },
  {
    id: 3,
    title: "Spam Content Detected",
    userName: "Mike Wilson",
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_129cc3c7b-1763293178950.png",
    userAvatarAlt: "Professional headshot of Caucasian man with gray hair wearing black suit",
    severity: "medium",
    violationType: "spam",
    detectedDate: "1 day ago",
    description: "User posted identical messages to multiple employers within short timeframe.",
    flaggedContent: "I am the best candidate for any position. Hire me now!",
    violations: ["Spam", "Mass Messaging"]
  }];


  // Mock data for audit logs
  const auditLogs = [
  {
    id: 1,
    action: "approval",
    description: "Approved candidate application for Sarah Mitchell (Senior Software Engineer)",
    adminName: "Admin User",
    adminAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14647e28f-1763295787553.png",
    adminAvatarAlt: "Professional headshot of Asian woman with long black hair wearing gray blazer",
    timestamp: "Jan 11, 2026 2:30 PM",
    ipAddress: "192.168.1.100"
  },
  {
    id: 2,
    action: "reveal",
    description: "Approved identity reveal request between TechCorp and anonymous candidate #1847",
    adminName: "Admin User",
    adminAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14647e28f-1763295787553.png",
    adminAvatarAlt: "Professional headshot of Asian woman with long black hair wearing gray blazer",
    timestamp: "Jan 11, 2026 1:15 PM",
    ipAddress: "192.168.1.100"
  },
  {
    id: 3,
    action: "suspension",
    description: "Suspended user account for Lisa Anderson due to anonymity policy violations",
    adminName: "Admin User",
    adminAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14647e28f-1763295787553.png",
    adminAvatarAlt: "Professional headshot of Asian woman with long black hair wearing gray blazer",
    timestamp: "Jan 11, 2026 11:45 AM",
    ipAddress: "192.168.1.100"
  },
  {
    id: 4,
    action: "rejection",
    description: "Rejected employer verification for ABC Recruiting Agency (not direct employer)",
    adminName: "Admin User",
    adminAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14647e28f-1763295787553.png",
    adminAvatarAlt: "Professional headshot of Asian woman with long black hair wearing gray blazer",
    timestamp: "Jan 11, 2026 10:20 AM",
    ipAddress: "192.168.1.100"
  },
  {
    id: 5,
    action: "update",
    description: "Updated platform anonymity detection rules to include new patterns",
    adminName: "Admin User",
    adminAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14647e28f-1763295787553.png",
    adminAvatarAlt: "Professional headshot of Asian woman with long black hair wearing gray blazer",
    timestamp: "Jan 11, 2026 9:00 AM",
    ipAddress: "192.168.1.100"
  }];


  // Mock data for analytics
  const userGrowthData = [
  { name: "Jan 1", candidates: 1200, employers: 85 },
  { name: "Jan 3", candidates: 1350, employers: 92 },
  { name: "Jan 5", candidates: 1520, employers: 98 },
  { name: "Jan 7", candidates: 1680, employers: 105 },
  { name: "Jan 9", candidates: 1790, employers: 112 },
  { name: "Jan 11", candidates: 1847, employers: 118 }];


  const matchSuccessData = [
  { name: "Week 1", matches: 45, reveals: 12 },
  { name: "Week 2", matches: 62, reveals: 18 },
  { name: "Week 3", matches: 78, reveals: 24 },
  { name: "Week 4", matches: 91, reveals: 31 }];


  const handleApprove = async (id) => {
    try {
      await accessRequestService?.updateStatus(id, 'approved');
      const updated = await accessRequestService?.getAll();
      setAccessRequests(updated);
      setStats((prev) => ({
        ...prev,
        pendingApprovals: prev?.pendingApprovals - 1
      }));
    } catch (err) {
      console.error('Error approving request:', err);
    }
  };

  const handleReject = async (id) => {
    try {
      await accessRequestService?.updateStatus(id, 'rejected');
      const updated = await accessRequestService?.getAll();
      setAccessRequests(updated);
      setStats((prev) => ({
        ...prev,
        pendingApprovals: prev?.pendingApprovals - 1
      }));
    } catch (err) {
      console.error('Error rejecting request:', err);
    }
  };

  const handleUpdateNotes = async (id, notes) => {
    try {
      await accessRequestService?.update(id, { adminNotes: notes });
      const updated = await accessRequestService?.getAll();
      setAccessRequests(updated);
    } catch (err) {
      console.error('Error updating notes:', err);
    }
  };

  const handleViewDetails = (item) => {
    setSelectedApproval(item);
  };

  const handleScheduleDemo = (demoData) => {
    alert(`Demo scheduled successfully!\n\nDate: ${demoData?.date}\nTime: ${demoData?.time}\nDuration: ${demoData?.duration} minutes\nMeeting Link: ${demoData?.meetingLink}`);
    setShowDemoScheduler(false);
    setSelectedApproval(null);
    // In production, this would save to database and send calendar invites
  };

  const handleAddNote = (note) => {
    setAdminNotes((prev) => ({
      ...prev,
      [note?.approvalId]: [...(prev?.[note?.approvalId] || []), note]
    }));
  };

  const handleDeleteNote = (approvalId, noteId) => {
    setAdminNotes((prev) => ({
      ...prev,
      [approvalId]: prev?.[approvalId]?.filter((note) => note?.id !== noteId) || []
    }));
  };

  const handleStatusChange = (approvalId, newStatus) => {
    setFollowUpStatuses((prev) => ({
      ...prev,
      [approvalId]: newStatus
    }));
    alert(`Status updated to: ${newStatus}`);
    // In production, this would update the database
  };

  const handleViewProfile = (user) => {
    // Simulate viewing user profile
    alert(`Viewing profile for: ${user?.name}\nEmail: ${user?.email}\nStatus: ${user?.status}\n\nThis would open a detailed profile view in production.`);
    // In production, this would navigate to a detailed profile page
  };

  const handleSuspend = (user) => {
    // Simulate suspension action
    const confirm = window.confirm(`Are you sure you want to suspend ${user?.name}?`);
    if (confirm) {
      alert(`User ${user?.name} has been suspended.`);
      // In production, this would call an API to suspend the user
    }
  };

  const handleActivate = (user) => {
    // Simulate activation action
    const confirm = window.confirm(`Are you sure you want to activate ${user?.name}?`);
    if (confirm) {
      alert(`User ${user?.name} has been activated.`);
      // In production, this would call an API to activate the user
    }
  };

  const handleInvestigate = (activity) => {
    // Simulate investigation action
    alert(`Investigating: ${activity?.title}\n\nThis would open an investigation panel with detailed logs and evidence in production.`);
    // In production, this would open a detailed investigation interface
  };

  const handleDismiss = (activity) => {
    // Simulate dismissing suspicious activity
    const confirm = window.confirm(`Are you sure you want to dismiss this activity?\n\n${activity?.title}`);
    if (confirm) {
      alert('Activity dismissed and marked as false positive.');
      // In production, this would update the activity status
    }
  };

  const renderDashboard = () =>
  <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard title="Total Users" value={stats?.totalUsers} icon="Users" iconBg="bg-gradient-to-br from-primary to-secondary" change="" changeType="neutral" />
        <StatCard title="Pending Approvals" value={stats?.pendingApprovals} icon="Clock" iconBg="bg-gradient-to-br from-warning to-warning/80" change="" changeType="neutral" />
        <StatCard title="Active Matches" value={stats?.activeMatches} icon="Heart" iconBg="bg-gradient-to-br from-success to-success/80" change="" changeType="neutral" />
        <StatCard title="Reveal Requests" value={stats?.revealRequests} icon="Eye" iconBg="bg-gradient-to-br from-accent to-accent/80" change="" changeType="neutral" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">Pending Approvals</h2>
          <Button variant="outline" size="sm" iconName="Filter" iconPosition="left">
            Filter
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {pendingApprovals?.map((approval) =>
        <PendingApprovalCard
          key={approval?.id}
          approval={approval}
          onApprove={handleApprove}
          onReject={handleReject}
          onViewDetails={handleViewDetails} />

        )}
        </div>
      </div>

      {selectedApproval &&
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
              <div>
                <h3 className="text-xl md:text-2xl font-semibold text-foreground">{selectedApproval?.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{selectedApproval?.type} • {selectedApproval?.submittedDate}</p>
              </div>
              <button
            onClick={() => setSelectedApproval(null)}
            className="p-2 hover:bg-muted rounded-lg transition-colors">

                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-4 md:p-6 space-y-6">
              {selectedApproval?.type === 'employer' &&
          <div className="bg-muted/50 rounded-xl p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-foreground">Company Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedApproval?.companyName &&
              <div>
                        <span className="text-xs text-muted-foreground block mb-1">Company Name</span>
                        <span className="text-sm text-foreground font-medium">{selectedApproval?.companyName}</span>
                      </div>
              }
                    {selectedApproval?.teamSize &&
              <div>
                        <span className="text-xs text-muted-foreground block mb-1">Team Size</span>
                        <span className="text-sm text-foreground font-medium">{selectedApproval?.teamSize} employees</span>
                      </div>
              }
                    {selectedApproval?.hrBudget &&
              <div>
                        <span className="text-xs text-muted-foreground block mb-1">Annual HR Budget</span>
                        <span className="text-sm text-foreground font-medium">
                          {selectedApproval?.hrBudget === 'under-100k' && 'Under $100K'}
                          {selectedApproval?.hrBudget === '100k-250k' && '$100K - $250K'}
                          {selectedApproval?.hrBudget === '250k-500k' && '$250K - $500K'}
                          {selectedApproval?.hrBudget === '500k-1m' && '$500K - $1M'}
                          {selectedApproval?.hrBudget === '1m-5m' && '$1M - $5M'}
                          {selectedApproval?.hrBudget === '5m+' && '$5M+'}
                        </span>
                      </div>
              }
                    {selectedApproval?.contactEmail &&
              <div>
                        <span className="text-xs text-muted-foreground block mb-1">Contact Email</span>
                        <span className="text-sm text-foreground font-medium">{selectedApproval?.contactEmail}</span>
                      </div>
              }
                  </div>
                </div>
          }

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <FollowUpStatusTracker
              approval={selectedApproval}
              currentStatus={followUpStatuses?.[selectedApproval?.id] || 'pending-review'}
              onStatusChange={handleStatusChange} />


                <AdminNotesPanel
              approval={selectedApproval}
              notes={adminNotes?.[selectedApproval?.id] || []}
              onAddNote={handleAddNote}
              onDeleteNote={(noteId) => handleDeleteNote(selectedApproval?.id, noteId)} />

              </div>

              {selectedApproval?.type === 'employer' &&
          <div className="flex gap-3">
                  <Button
              variant="default"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => setShowDemoScheduler(true)}
              className="flex-1">

                    Schedule Demo
                  </Button>
                  <Button
              variant="success"
              iconName="Check"
              iconPosition="left"
              onClick={() => {
                handleApprove(selectedApproval);
                setSelectedApproval(null);
              }}
              className="flex-1">

                    Approve Access
                  </Button>
                  <Button
              variant="destructive"
              iconName="X"
              iconPosition="left"
              onClick={() => {
                handleReject(selectedApproval);
                setSelectedApproval(null);
              }}
              className="flex-1">

                    Reject
                  </Button>
                </div>
          }
            </div>
          </div>
        </div>
    }

      {showDemoScheduler && selectedApproval &&
    <DemoScheduler
      approval={selectedApproval}
      onSchedule={handleScheduleDemo}
      onClose={() => setShowDemoScheduler(false)} />

    }

      <div>
        <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">Recent Activity</h2>
        <AuditLogTable logs={auditLogs?.slice(0, 5)} />
      </div>
    </div>;


  const renderCandidates = () =>
  <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">Candidate Management</h2>
        <Button variant="default" size="sm" iconName="Download" iconPosition="left">
          Export Data
        </Button>
      </div>
      <UserTable
      users={candidates}
      type="candidates"
      onViewProfile={handleViewProfile}
      onSuspend={handleSuspend}
      onActivate={handleActivate} />

    </div>;


  const renderEmployers = () =>
  <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">Employer Management</h2>
        <Button variant="default" size="sm" iconName="Download" iconPosition="left">
          Export Data
        </Button>
      </div>
      <UserTable
      users={employers}
      type="employers"
      onViewProfile={handleViewProfile}
      onSuspend={handleSuspend}
      onActivate={handleActivate} />

    </div>;


  const renderRevealApprovals = () =>
  <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">Identity Reveal Requests</h2>
        <Button variant="outline" size="sm" iconName="Filter" iconPosition="left">
          Filter
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {revealRequests?.map((request) =>
      <RevealRequestCard
        key={request?.id}
        request={request}
        onApprove={handleApprove}
        onReject={handleReject}
        onViewDetails={handleViewDetails} />

      )}
      </div>
    </div>;


  const renderSuspiciousActivity = () =>
  <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">Suspicious Activities</h2>
        <Button variant="outline" size="sm" iconName="Filter" iconPosition="left">
          Filter
        </Button>
      </div>
      <div className="space-y-4">
        {suspiciousActivities?.map((activity) =>
      <SuspiciousActivityCard
        key={activity?.id}
        activity={activity}
        onInvestigate={handleInvestigate}
        onDismiss={handleDismiss}
        onSuspend={handleSuspend} />

      )}
      </div>
    </div>;


  const renderAccessRequests = () =>
  <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">Access Requests Waitlist</h2>
        <div className="flex gap-2">
          <Button
          variant="outline"
          size="sm"
          iconName="RefreshCw"
          iconPosition="left"
          onClick={handleRefresh}
          disabled={isRefreshing}>

            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="outline" size="sm" iconName="Filter" iconPosition="left">
            Filter
          </Button>
          <Button variant="default" size="sm" iconName="CheckCircle" iconPosition="left">
            Batch Approve
          </Button>
        </div>
      </div>
      {waitlistRequests?.length === 0 ?
    <div className="bg-card rounded-2xl p-8 md:p-12 text-center elevation-sm">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No Access Requests Yet</h3>
          <p className="text-muted-foreground">
            When users submit access requests from the website, they will appear here for review.
          </p>
        </div> :

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {waitlistRequests?.map((request) =>
      <WaitlistCard
        key={request?.id}
        request={request}
        onApprove={handleApprove}
        onReject={handleReject}
        onViewDetails={handleViewDetails} />

      )}
        </div>
    }
    </div>;


  const renderAnalytics = () =>
  <div className="space-y-6">
      <h2 className="text-xl md:text-2xl font-semibold text-foreground">Platform Analytics</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AnalyticsChart
        title="User Growth Trend"
        data={userGrowthData}
        type="line"
        dataKeys={[
        { key: "candidates", name: "Candidates", color: "var(--color-primary)" },
        { key: "employers", name: "Employers", color: "var(--color-accent)" }]
        } />

        <AnalyticsChart
        title="Match Success Rate"
        data={matchSuccessData}
        type="bar"
        dataKeys={[
        { key: "matches", name: "Total Matches", color: "var(--color-primary)" },
        { key: "reveals", name: "Identity Reveals", color: "var(--color-success)" }]
        } />

      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard
        title="Avg. Time to Match"
        value="4.2 days"
        change="-15%"
        changeType="positive"
        icon="Clock"
        iconBg="bg-gradient-to-br from-primary to-secondary" />

        <StatCard
        title="Match Success Rate"
        value="68%"
        change="+5%"
        changeType="positive"
        icon="TrendingUp"
        iconBg="bg-gradient-to-br from-success to-success/80" />

        <StatCard
        title="Avg. Subscription Value"
        value="$249"
        change="+12%"
        changeType="positive"
        icon="DollarSign"
        iconBg="bg-gradient-to-br from-accent to-accent/80" />

        <StatCard
        title="Platform Engagement"
        value="87%"
        change="+3%"
        changeType="positive"
        icon="Activity"
        iconBg="bg-gradient-to-br from-warning to-warning/80" />

      </div>
    </div>;


  const renderAuditLogs = () =>
  <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">Audit Logs</h2>
        <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
          Export Logs
        </Button>
      </div>
      <AuditLogTable logs={auditLogs} />
    </div>;


  const renderSettings = () =>
  <div>
      <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-6">Platform Settings</h2>
      <div className="bg-card rounded-2xl p-6 elevation-sm">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Anonymity Detection Rules</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Configure automated detection patterns for anonymity violations
            </p>
            <Button variant="default" iconName="Settings" iconPosition="left">
              Configure Rules
            </Button>
          </div>
          
          <div className="border-t border-border pt-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Approval Workflows</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Manage approval processes for candidates, employers, and reveals
            </p>
            <Button variant="default" iconName="GitBranch" iconPosition="left">
              Manage Workflows
            </Button>
          </div>
          
          <div className="border-t border-border pt-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Notification Settings</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Configure admin notifications and alerts
            </p>
            <Button variant="default" iconName="Bell" iconPosition="left">
              Configure Notifications
            </Button>
          </div>
        </div>
      </div>
    </div>;


  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderDashboard();
      case 'candidates':
        return renderCandidates();
      case 'employers':
        return renderEmployers();
      case 'access-requests':
        return renderAccessRequests();
      case 'reveal-approvals':
        return renderRevealApprovals();
      case 'reported':
        return renderSuspiciousActivity();
      case 'analytics':
        return renderAnalytics();
      case 'audit':
        return renderAuditLogs();
      case 'settings':
        return renderSettings();
      case 'verification':
        return <VerificationDashboard />;
      default:
        return renderDashboard();
    }
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);

    // Load specific data based on section
    if (section === 'verification') {



      // Verification dashboard handles its own data loading
    }};React.useEffect(() => {
    const hash = window.location?.hash?.replace('#', '');
    if (hash) {
      setActiveSection(hash);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <AdminSidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          activeSection={activeSection}
          onNavigate={setActiveSection} />

        <div className="ml-64 p-8">
          <div className="text-center text-white">
            <p>Loading admin data...</p>
          </div>
        </div>
      </div>);

  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <AdminSidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          activeSection={activeSection}
          onNavigate={setActiveSection} />

        <div className="ml-64 p-8">
          <div className="text-center text-red-400">
            <p>Error loading admin data: {error}</p>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background flex">
      <AdminSidebar isCollapsed={isSidebarCollapsed} onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
      
      <div className={`flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
        <div className="p-4 md:p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Admin Control Panel</h1>
            <p className="text-muted-foreground">Manage users, approvals, and platform settings</p>
          </div>

          {/* Navigation Tabs */}
          <div className="bg-card rounded-2xl p-2 elevation-sm mb-8 overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              <button
                onClick={() => handleSectionChange('dashboard')}
                className={`px-4 py-2 rounded-xl font-medium transition-all ${
                activeSection === 'dashboard' ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'}`
                }>

                Dashboard
              </button>
              <button
                onClick={() => handleSectionChange('verification')}
                className={`px-4 py-2 rounded-xl font-medium transition-all ${
                activeSection === 'verification' ?
                'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'}`
                }>

                Verification
              </button>
              <button
                onClick={() => handleSectionChange('access-requests')}
                className={`px-4 py-2 rounded-xl font-medium transition-all ${
                activeSection === 'access-requests' ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'}`
                }>

                Access Requests
              </button>
              <button
                onClick={() => handleSectionChange('candidates')}
                className={`px-4 py-2 rounded-xl font-medium transition-all ${
                activeSection === 'candidates' ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'}`
                }>

                Candidates
              </button>
              <button
                onClick={() => handleSectionChange('employers')}
                className={`px-4 py-2 rounded-xl font-medium transition-all ${
                activeSection === 'employers' ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted'}`
                }>

                Employers
              </button>
            </div>
          </div>

          {/* Verification Dashboard Section */}
          {activeSection === 'verification' &&
          <VerificationDashboard />
          }

          {/* Content */}
          {renderContent()}
        </div>
      </div>
    </div>);

};

export default AdminControlPanel;